import type { EvalRunData } from "@global/types/ai-evals";
import { Box, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Tooltip, Typography } from "@mui/material";
import React, { useMemo } from "react";
import { costOf, countLabel, counts, groupsOf, pct, type RunKey, rowsOf, score, scopeOf, VERDICTS } from "./scoring";
import { Delta, HeatLegend, heat, ScoreBar, StatCard, VerdictCounts } from "./ui";

interface OverviewProps {
  data: EvalRunData;
  baseline: EvalRunData | null;
  onOpenGroup: (group: string) => void;
  onOpenCheck: (id: string) => void;
  onOpenRun: (key: RunKey) => void;
}

export const Overview: React.FC<OverviewProps> = ({ data, baseline, onOpenGroup, onOpenCheck, onOpenRun }) => {
  const now = useMemo(() => rowsOf(data), [data]);
  const before = useMemo(() => rowsOf(baseline), [baseline]);
  const groups = groupsOf(data);
  const checkText = new Map(data.checks.map((c) => [c.id, c.text]));
  const scenarioIds = [...new Set(data.results.runs.map((r) => r.scenarioId))].sort();
  const inScope = (rows: typeof now, scope: "interview" | "case") => score(rows.filter((r) => scopeOf(r.group) === scope));
  const judged = now.filter((r) => r.by === "judge");
  const tieBreaks = judged.filter((r) => r.votes && new Set(r.votes).size > 1).length;
  const failures = now.filter((r) => r.verdict === "fail");
  const moved = baseline
    ? [...new Set(now.map((r) => r.id))]
        .map((id) => ({ id, now: score(now.filter((r) => r.id === id)), before: score(before.filter((r) => r.id === id)) }))
        .filter((c): c is { id: string; now: number; before: number } => c.now !== null && c.before !== null && Math.abs(c.now - c.before) >= 0.25)
        .sort((a, b) => a.now - a.before - (b.now - b.before))
    : [];

  return (
    <Box>
      <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
        {(["interview", "case"] as const).map((scope) => (
          <StatCard
            key={scope}
            label={scope === "interview" ? "Interview score" : "Case score"}
            value={pct(inScope(now, scope))}
            detail={
              <>
                {baseline && <Delta now={inScope(now, scope)} before={inScope(before, scope)} />}
                <VerdictCounts rows={now.filter((r) => scopeOf(r.group) === scope)} />
              </>
            }
          />
        ))}
        <StatCard
          label="Failed checks"
          value={failures.length}
          detail={baseline && <Typography variant="body2" color="text.secondary">baseline {before.filter((r) => r.verdict === "fail").length}</Typography>}
        />
        <StatCard
          label="Judge tie-breaks"
          value={`${judged.length ? Math.round((tieBreaks / judged.length) * 100) : 0} %`}
          detail={<Typography variant="body2" color="text.secondary">{tieBreaks} of {judged.length} judged checks</Typography>}
        />
        <StatCard
          label="Eval cost"
          value={`$${costOf(data).toFixed(2)}`}
          detail={Object.entries(data.results.usage).map(([key, u]) => (
            <Typography key={key} variant="body2" color="text.secondary">
              {key}: ${u.usd.toFixed(2)}
            </Typography>
          ))}
        />
      </Box>
      <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5 }}>
        Score: pass = 100, concerns = 50, fail = 0, averaged over the checks that apply (N/A doesn't count). Shares of pass, concerns and fail are of the applicable checks, the N/A share of all. Δ in points against the baseline.
      </Typography>

      <Typography variant="h6" sx={{ mt: 4, mb: 1 }}>
        Score by group
      </Typography>
      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Group</TableCell>
              <TableCell />
              <TableCell align="right">Score</TableCell>
              {baseline && <TableCell align="right">Baseline</TableCell>}
              {baseline && <TableCell align="right">Δ</TableCell>}
              <TableCell align="right">Pass</TableCell>
              <TableCell align="right">Concerns</TableCell>
              <TableCell align="right">Fail</TableCell>
              <TableCell align="right">N/A</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {groups.map(([group, title]) => {
              const rows = now.filter((r) => r.group === group);
              const s = score(rows);
              const b = baseline ? score(before.filter((r) => r.group === group)) : null;
              const c = counts(rows);
              return (
                <TableRow key={group} hover sx={{ cursor: "pointer" }} onClick={() => onOpenGroup(group)}>
                  <TableCell>
                    <b>{group}</b> {title}
                    <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                      {scopeOf(group)}
                    </Typography>
                  </TableCell>
                  <TableCell sx={{ width: "35%" }}>
                    <ScoreBar now={s} before={b} />
                  </TableCell>
                  <TableCell align="right">{pct(s)}</TableCell>
                  {baseline && <TableCell align="right">{pct(b)}</TableCell>}
                  {baseline && (
                    <TableCell align="right">
                      <Delta now={s} before={b} />
                    </TableCell>
                  )}
                  {VERDICTS.map((v) => (
                    <TableCell key={v} align="right" sx={{ whiteSpace: "nowrap", fontVariantNumeric: "tabular-nums", color: c[v] ? "text.primary" : "text.disabled" }}>
                      {countLabel(c, v)}
                    </TableCell>
                  ))}
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
      <Box sx={{ display: "flex", gap: 2, mt: 1, color: "text.secondary", fontSize: 12, alignItems: "center" }}>
        <span>
          <Box component="span" sx={{ display: "inline-block", width: 10, height: 10, bgcolor: "primary.main", borderRadius: "2px", mr: 0.75 }} />
          This run
        </span>
        {baseline && (
          <span>
            <Box component="span" sx={{ display: "inline-block", width: 2, height: 12, bgcolor: "text.primary", mr: 0.75, verticalAlign: "-2px" }} />
            Baseline
          </span>
        )}
      </Box>

      <Typography variant="h6" sx={{ mt: 4, mb: 1 }}>
        Scenarios × groups
      </Typography>
      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Scenario</TableCell>
              {groups.map(([group, title]) => (
                <Tooltip key={group} title={title}>
                  <TableCell align="center">{group}</TableCell>
                </Tooltip>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {scenarioIds.map((scenarioId) => (
              <TableRow key={scenarioId}>
                <TableCell sx={{ cursor: "pointer", color: "primary.main", whiteSpace: "nowrap" }} onClick={() => onOpenRun({ scenarioId, run: 1 })}>
                  {scenarioId}
                </TableCell>
                {groups.map(([group, title]) => {
                  const cell = now.filter((r) => r.scenarioId === scenarioId && r.group === group);
                  const s = score(cell);
                  const b = baseline ? score(before.filter((r) => r.scenarioId === scenarioId && r.group === group)) : null;
                  const c = counts(cell);
                  const mix = `${c.pass} pass · ${c.concerns} concerns · ${c.fail} fail · ${c.na} n/a`;
                  return (
                    <Tooltip key={group} title={`${scenarioId} · ${group} ${title}: ${pct(s)}${baseline ? ` (baseline ${pct(b)})` : ""} — ${mix}`}>
                      <TableCell align="center" sx={{ ...heat(s), border: "2px solid", borderColor: "background.paper", fontVariantNumeric: "tabular-nums" }}>
                        {pct(s)}
                      </TableCell>
                    </Tooltip>
                  );
                })}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <HeatLegend />

      {baseline && (
        <>
          <Typography variant="h6" sx={{ mt: 4, mb: 1 }}>
            Checks that moved by 25 points or more
          </Typography>
          {moved.length === 0 ? (
            <Typography color="text.secondary">None.</Typography>
          ) : (
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Check</TableCell>
                    <TableCell align="right">Baseline</TableCell>
                    <TableCell align="right">Now</TableCell>
                    <TableCell align="right">Δ</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {moved.map((c) => (
                    <TableRow key={c.id} hover sx={{ cursor: "pointer" }} onClick={() => onOpenCheck(c.id)}>
                      <TableCell>
                        <b>{c.id}</b> {checkText.get(c.id)}
                      </TableCell>
                      <TableCell align="right">{pct(c.before)}</TableCell>
                      <TableCell align="right">{pct(c.now)}</TableCell>
                      <TableCell align="right">
                        <Delta now={c.now} before={c.before} />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </>
      )}

      <Typography variant="h6" sx={{ mt: 4, mb: 1 }}>
        Failures ({failures.length})
      </Typography>
      {failures.length === 0 ? (
        <Typography color="text.secondary">None.</Typography>
      ) : (
        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Check</TableCell>
                <TableCell>Run</TableCell>
                <TableCell>Evidence</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {failures.map((f) => (
                <TableRow key={`${f.id}-${f.scenarioId}-${f.run}`} hover sx={{ cursor: "pointer" }} onClick={() => onOpenRun(f)}>
                  <TableCell>
                    <b>{f.id}</b> {checkText.get(f.id)}
                  </TableCell>
                  <TableCell sx={{ whiteSpace: "nowrap" }}>
                    {f.scenarioId} #{f.run}
                  </TableCell>
                  <TableCell sx={{ color: "text.secondary" }}>{f.evidence}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
};
